import { useCallback, useEffect, useRef, useState } from 'react';
import { AssistantStreamError, streamChat } from './stream';
import type { AssistantUsage, ChatMessage, PendingAction } from './types';

export interface TranscriptEntry {
  role: 'user' | 'assistant';
  content: string;
  actions?: PendingAction[];
  status?: 'streaming' | 'error';
}

export interface UseAssistantChatOptions {
  workspaceId: string | null;
  // Kept translation-free: the component resolves these via useTranslation and passes the strings in.
  errorText: { generic: string; refusal: string; limit: string };
}

export function useAssistantChat(opts: UseAssistantChatOptions) {
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [toolNote, setToolNote] = useState<string | null>(null);
  const [usage, setUsage] = useState<AssistantUsage | null>(null);
  const transcriptRef = useRef<TranscriptEntry[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const applyTranscript = useCallback((updater: (prev: TranscriptEntry[]) => TranscriptEntry[]) => {
    transcriptRef.current = updater(transcriptRef.current);
    setTranscript(transcriptRef.current);
  }, []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setBusy(false);
    setToolNote(null);
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    transcriptRef.current = [];
    setTranscript([]);
    setBusy(false);
    setToolNote(null);
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      const workspaceId = opts.workspaceId;
      if (!trimmed || busy || !workspaceId) return;

      const outgoing: ChatMessage[] = [
        ...transcriptRef.current.filter((m) => m.status !== 'error').map((m) => ({ role: m.role, content: m.content })),
        { role: 'user' as const, content: trimmed },
      ];

      const controller = new AbortController();
      abortRef.current = controller;
      setBusy(true);
      setToolNote(null);

      applyTranscript((prev) => [...prev, { role: 'user', content: trimmed }, { role: 'assistant', content: '', status: 'streaming' }]);

      const onText = (delta: string) => {
        applyTranscript((prev) => {
          const next = [...prev];
          const last = next[next.length - 1]!;
          next[next.length - 1] = { ...last, content: last.content + delta };
          return next;
        });
      };
      const onTool = (name: string) => setToolNote(name);

      try {
        const result = await streamChat({ workspaceId, messages: outgoing, signal: controller.signal, onText, onTool });
        applyTranscript((prev) => {
          const next = [...prev];
          const last = next[next.length - 1]!;
          next[next.length - 1] = { ...last, content: result.message || last.content, status: undefined, actions: result.actions };
          return next;
        });
        if (result.usage) setUsage(result.usage);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          applyTranscript((prev) => {
            const next = [...prev];
            const last = next[next.length - 1]!;
            next[next.length - 1] = { ...last, status: undefined };
            return next;
          });
        } else {
          const code = err instanceof AssistantStreamError ? err.code : 'INTERNAL';
          const content =
            code === 'REFUSAL' ? opts.errorText.refusal : code === 'RATE_LIMITED' ? opts.errorText.limit : opts.errorText.generic;
          applyTranscript((prev) => {
            const next = [...prev];
            next[next.length - 1] = { role: 'assistant', content, status: 'error' };
            return next;
          });
        }
      } finally {
        abortRef.current = null;
        setBusy(false);
        setToolNote(null);
      }
    },
    [applyTranscript, busy, opts.workspaceId, opts.errorText],
  );

  return { transcript, busy, toolNote, usage, send, stop, reset };
}
